import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ClientInfoSection from './components/ClientInfoSection';
import KitchenConfigSection from './components/KitchenConfigSection';
import WorktopSection from './components/WorktopSection';
import ApplianceSection from './components/ApplianceSection';
import AdditionalServicesSection from './components/AdditionalServicesSection';
import PricingSummary from './components/PricingSummary';
import Button from '../../components/ui/Button';
import { clientService } from '../../services/clientService';
import { quoteService } from '../../services/quoteService';
import { systemService } from '../../services/systemService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';


const QuoteCalculator = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [clients, setClients] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  
  // Check if user is in restricted mode (not fully authenticated admin)
  const isRestrictedMode = !user || user?.role !== 'admin';
  
  // Section collapse states
  const [collapsedSections, setCollapsedSections] = useState({
    client: false,
    kitchen: false,
    worktop: false,
    appliance: true,
    services: true,
    pricing: false
  });

  // Form data states
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    phone: '',
    email: ''
  });

  const [kitchenConfig, setKitchenConfig] = useState({
    style: '',
    upperCabinets: 0,
    lowerCabinets: 0,
    columns: 0,
    upperCorners: 0,
    lowerCorners: 0,
    baseboards: 0,
    jambs: 0,
    fillers: 0,
    sidePanels: 0,
    backsplash: 0
  });

  const [worktopConfig, setWorktopConfig] = useState({
    type: '',
    installationShape: '',
    cutouts: 0,
    cooktopIntegration: false,
    sinkIntegration: false
  });

  const [applianceConfig, setApplianceConfig] = useState({
    cooktop: false,
    oven: false,
    microwave: false,
    dishwasher: false,
    washingMachine: false,
    dryer: false,
    refrigerator: false,
    freezer: false,
    rangeHood: false
  });

  const [servicesConfig, setServicesConfig] = useState({
    plumbingModification: false,
    lightingInstallation: false,
    electricalModification: false
  });

  const [pricingConfig, setPricingConfig] = useState({
    clientCategory: '',
    vat: ''
  });

  const [errors, setErrors] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Calculate total price based on configuration
  useEffect(() => {
    calculateTotalPrice();
  }, [kitchenConfig, worktopConfig, applianceConfig, servicesConfig, pricingConfig, systemSettings]);

  const loadInitialData = async () => {
    try {
      // Load clients for selection
      const { clients: clientsData } = await clientService?.getClients();
      setClients(clientsData || []);

      // Load system settings for pricing
      const { settings } = await systemService?.getSystemSettings();
      setSystemSettings(settings || {});
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const calculateTotalPrice = () => {
    if (!systemSettings || Object.keys(systemSettings)?.length === 0) {
      return;
    }

    const quoteData = {
      upper_cabinets: kitchenConfig?.upperCabinets,
      lower_cabinets: kitchenConfig?.lowerCabinets,
      columns: kitchenConfig?.columns,
      upper_corners: kitchenConfig?.upperCorners,
      lower_corners: kitchenConfig?.lowerCorners,
      baseboards: kitchenConfig?.baseboards,
      jambs: kitchenConfig?.jambs,
      fillers: kitchenConfig?.fillers,
      side_panels: kitchenConfig?.sidePanels,
      backsplash: kitchenConfig?.backsplash,
      worktop_type: worktopConfig?.type,
      installation_shape: worktopConfig?.installationShape,
      cutouts: worktopConfig?.cutouts,
      plumbing_modification: servicesConfig?.plumbingModification,
      lighting_installation: servicesConfig?.lightingInstallation,
      electrical_modification: servicesConfig?.electricalModification,
      client_category: pricingConfig?.clientCategory,
      vat_rate: pricingConfig?.vat
    };

    const pricing = quoteService?.calculateQuotePrice(quoteData, systemSettings);
    setTotalPrice(pricing?.totalPrice || 0);
  };

  // Toggle section collapse
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Client info validation
    if (!clientInfo?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
    if (!clientInfo?.lastName?.trim()) newErrors.lastName = 'Le nom est requis';
    if (!clientInfo?.address?.trim()) newErrors.address = 'L\'adresse est requise';
    if (!clientInfo?.phone?.trim()) newErrors.phone = 'Le téléphone est requis';
    if (!clientInfo?.email?.trim()) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/?.test(clientInfo?.email)) newErrors.email = 'Email invalide';

    // Kitchen config validation
    if (!kitchenConfig?.style) newErrors.style = 'Le style de cuisine est requis';

    // Worktop validation
    if (!worktopConfig?.type) newErrors.type = 'Le type de plan de travail est requis';
    if (!worktopConfig?.installationShape) newErrors.installationShape = 'La configuration est requise';

    // Pricing validation
    if (!pricingConfig?.clientCategory) newErrors.clientCategory = 'La catégorie client est requise';
    if (!pricingConfig?.vat) newErrors.vat = 'La TVA est requise';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Generate quote
  const handleGenerateQuote = async () => {
    if (!validateForm()) {
      // Expand sections with errors
      if (errors?.firstName || errors?.lastName || errors?.address || errors?.phone || errors?.email) {
        setCollapsedSections(prev => ({ ...prev, client: false }));
      }
      if (errors?.style) {
        setCollapsedSections(prev => ({ ...prev, kitchen: false }));
      }
      if (errors?.type || errors?.installationShape) {
        setCollapsedSections(prev => ({ ...prev, worktop: false }));
      }
      return;
    }

    setIsGenerating(true);
    
    try {
      // First create or get the client
      let clientId;
      const existingClient = clients?.find(c => 
        c?.email === clientInfo?.email || 
        (c?.first_name === clientInfo?.firstName && c?.last_name === clientInfo?.lastName)
      );

      if (existingClient) {
        clientId = existingClient?.id;
      } else {
        const { client, error: clientError } = await clientService?.createClient({
          first_name: clientInfo?.firstName,
          last_name: clientInfo?.lastName,
          email: clientInfo?.email,
          phone: clientInfo?.phone,
          address: clientInfo?.address,
          category: pricingConfig?.clientCategory,
          created_by: user?.id
        });

        if (clientError) {
          throw new Error(clientError);
        }

        clientId = client?.id;
      }

      // Create quote data
      const quoteData = {
        client_id: clientId,
        created_by: user?.id,
        status: 'draft',
        kitchen_style: kitchenConfig?.style,
        upper_cabinets: kitchenConfig?.upperCabinets,
        lower_cabinets: kitchenConfig?.lowerCabinets,
        columns: kitchenConfig?.columns,
        upper_corners: kitchenConfig?.upperCorners,
        lower_corners: kitchenConfig?.lowerCorners,
        baseboards: kitchenConfig?.baseboards,
        jambs: kitchenConfig?.jambs,
        fillers: kitchenConfig?.fillers,
        side_panels: kitchenConfig?.sidePanels,
        backsplash: kitchenConfig?.backsplash,
        worktop_type: worktopConfig?.type,
        installation_shape: worktopConfig?.installationShape,
        cutouts: worktopConfig?.cutouts,
        cooktop_integration: worktopConfig?.cooktopIntegration,
        sink_integration: worktopConfig?.sinkIntegration,
        plumbing_modification: servicesConfig?.plumbingModification,
        lighting_installation: servicesConfig?.lightingInstallation,
        electrical_modification: servicesConfig?.electricalModification,
        client_category: pricingConfig?.clientCategory,
        vat_rate: pricingConfig?.vat,
        total_price: totalPrice
      };

      // Create appliances array
      const appliances = Object.entries(applianceConfig)
        ?.filter(([key, included]) => included)
        ?.map(([type]) => ({ type, included: true }));

      // Create the quote
      const { quote, error: quoteError } = await quoteService?.createQuote(quoteData, appliances);

      if (quoteError) {
        throw new Error(quoteError);
      }

      // Success notification
      alert(`Devis généré avec succès !\nNuméro: ${quote?.quote_number}\nTotal: ${totalPrice?.toLocaleString('fr-FR', { 
        style: 'currency', 
        currency: 'EUR' 
      })}`);
      
    } catch (error) {
      alert(`Erreur lors de la génération du devis: ${error?.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset form
  const handleResetForm = () => {
    setClientInfo({
      firstName: '',
      lastName: '',
      address: '',
      phone: '',
      email: ''
    });
    setKitchenConfig({
      style: '',
      upperCabinets: 0,
      lowerCabinets: 0,
      columns: 0,
      upperCorners: 0,
      lowerCorners: 0,
      baseboards: 0,
      jambs: 0,
      fillers: 0,
      sidePanels: 0,
      backsplash: 0
    });
    setWorktopConfig({
      type: '',
      installationShape: '',
      cutouts: 0,
      cooktopIntegration: false,
      sinkIntegration: false
    });
    setApplianceConfig({
      cooktop: false,
      oven: false,
      microwave: false,
      dishwasher: false,
      washingMachine: false,
      dryer: false,
      refrigerator: false,
      freezer: false,
      rangeHood: false
    });
    setServicesConfig({
      plumbingModification: false,
      lightingInstallation: false,
      electricalModification: false
    });
    setPricingConfig({
      clientCategory: '',
      vat: ''
    });
    setErrors({});
    setTotalPrice(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        restrictedMode={isRestrictedMode}
      />
      <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="px-4 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs />
            
            {/* Restricted Mode Banner */}
            {isRestrictedMode && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="p-1 bg-amber-100 rounded-full">
                      <Icon name="Info" size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-amber-800 mb-1">Mode Calculateur Restreint</h3>
                      <p className="text-sm text-amber-700">
                        Vous êtes en mode calculateur uniquement. Pour accéder à toutes les fonctionnalités administratives, veuillez vous connecter.
                      </p>
                    </div>
                  </div>
                  <Button 
                    as="a" 
                    href="/login" 
                    variant="outline" 
                    size="sm"
                    iconName="LogIn"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    Se connecter
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Calculateur de Devis
                </h1>
                <p className="text-text-secondary mt-1">
                  Générez des devis professionnels pour vos installations de cuisine
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={handleResetForm}
                  className="hidden sm:flex"
                >
                  Réinitialiser
                </Button>
                
                <Button
                  variant="ghost"
                  iconName="RotateCcw"
                  onClick={handleResetForm}
                  className="sm:hidden"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Form Sections */}
              <div className="lg:col-span-2 space-y-6">
                <ClientInfoSection
                  clientInfo={clientInfo}
                  onClientInfoChange={setClientInfo}
                  errors={errors}
                  isCollapsed={collapsedSections?.client}
                  onToggleCollapse={() => toggleSection('client')}
                />

                <KitchenConfigSection
                  kitchenConfig={kitchenConfig}
                  onKitchenConfigChange={setKitchenConfig}
                  errors={errors}
                  isCollapsed={collapsedSections?.kitchen}
                  onToggleCollapse={() => toggleSection('kitchen')}
                />

                <WorktopSection
                  worktopConfig={worktopConfig}
                  onWorktopConfigChange={setWorktopConfig}
                  errors={errors}
                  isCollapsed={collapsedSections?.worktop}
                  onToggleCollapse={() => toggleSection('worktop')}
                />

                <ApplianceSection
                  applianceConfig={applianceConfig}
                  onApplianceConfigChange={setApplianceConfig}
                  isCollapsed={collapsedSections?.appliance}
                  onToggleCollapse={() => toggleSection('appliance')}
                />

                <AdditionalServicesSection
                  servicesConfig={servicesConfig}
                  onServicesConfigChange={setServicesConfig}
                  isCollapsed={collapsedSections?.services}
                  onToggleCollapse={() => toggleSection('services')}
                />
              </div>

              {/* Pricing Summary */}
              <div className="lg:col-span-1">
                <PricingSummary
                  pricingConfig={pricingConfig}
                  onPricingConfigChange={setPricingConfig}
                  totalPrice={totalPrice}
                  onGenerateQuote={handleGenerateQuote}
                  isGenerating={isGenerating}
                  isCollapsed={collapsedSections?.pricing}
                  onToggleCollapse={() => toggleSection('pricing')}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuoteCalculator;